import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MobileFrame } from "@/components/MobileFrame";
import { Hairline, Icon, Tape } from "@/components/primitives";

/**
 * FAQ — a fully-fleshed reference page covering the most common questions
 * Collectors ask once they get past onboarding. Grouped by topic, each entry
 * is a collapsible accordion to keep the scroll lightweight on mobile.
 */

interface FaqEntry { q: string; a: string }
interface FaqSection { id: string; title: string; entries: FaqEntry[] }

const SECTIONS: FaqSection[] = [
  {
    id: "scanning",
    title: "Scanning & verification",
    entries: [
      {
        q: "What does fig actually do when I scan something?",
        a: "Tap the centre button on the dock and photograph your garment — a tag, a label, or the whole piece. fig reads the maker, era, and whether it looks like the real thing, then writes the result into your closet so you actually know what you own. The verdict is paired with the reference passages it leaned on, so you can see why fig called it that way.",
      },
      {
        q: "What photos give the best result?",
        a: "The brand tag, the care / composition tag, the country-of-origin tag, a wide shot of the whole garment, and any zippers, buttons, or stitching that look brand-specific. Natural light beats flash. If you only have one photo, make it the brand tag.",
      },
      {
        q: "Why did my scan come back as FAIL?",
        a: "FAIL doesn't always mean 'fake' — it means at least one signal (font, label era, stitching, hardware, country) doesn't match what the reference says for that brand or year. Open the verdict notes to see exactly which signal flagged and which reference passage was cited. If you disagree, retake the tag in better light and re-scan — or send a correction so fig can learn.",
      },
      {
        q: "What if fig isn't sure?",
        a: "Some scans come back inconclusive — the evidence doesn't push hard enough in either direction. You'll see a 'needs more photos' prompt with exactly which tag or detail would help. Add the shot, re-run, and the verdict updates.",
      },
      {
        q: "Can I edit a scan after the fact?",
        a: "Yes. Open the piece from your closet and you can update the title, brand, condition, photos, and notes. The original verdict stays attached so the history is always there.",
      },
      {
        q: "How do bulk scans work?",
        a: "From the dock, hit Scan → Bulk to upload up to 20 pieces in one pass. Each one runs through the same verification flow individually; you'll get a single review screen at the end to triage results. Useful when you've just thrifted a haul and want to clear it in one sitting.",
      },
    ],
  },
  {
    id: "reference",
    title: "The reference & verdicts",
    entries: [
      {
        q: "What's 'the reference'?",
        a: "fig's verdicts are grounded in a body of authentication reference material — brand histories, label eras, hardware specs, country-of-manufacture timelines, fabric and care-tag conventions. When the engine returns a verdict, it cites the specific reference passage it used. fig never invents reasoning, and if the reference is silent on a signal, the verdict says so explicitly.",
      },
      {
        q: "Why does every verdict point to a reference passage?",
        a: "So you can read the same source fig read. If a verdict flags a hangtag font as wrong for 1978 Comme des Garçons, you can open the cited passage and see the documented font change yourself. The goal is to teach, not just judge — by the tenth scan you should know what to look at, not just what fig told you.",
      },
      {
        q: "What does 'reference silent' mean?",
        a: "It means the reference doesn't have authoritative information on that specific detail for that brand or era. fig flags it rather than guessing. You can still reach a verdict from other signals, but that particular one is marked as not-yet-documented so you know where the confidence is coming from.",
      },
      {
        q: "Can I push back if I think the verdict is wrong?",
        a: "Yes. Under any verdict you'll find a 'Send a correction' option — describe what you think the engine missed (e.g. 'this label era is correct, the tag was reissued in 1992'), and it goes to the reference review queue. Confirmed corrections feed back into the reference so future scans benefit.",
      },
      {
        q: "Where can I read the reference myself?",
        a: "The Field Guide (Profile → Field guide, or the 'Field guide' button on this page) collects the reference passages alongside how to use them — what to photograph for each brand, which signals matter, and how to interpret them. It's the same source the engine reads.",
      },
    ],
  },
  {
    id: "pricing",
    title: "Pricing & comps",
    entries: [
      {
        q: "How is the comp range calculated?",
        a: "Every verified piece comes with a comp range pulled from real-time market listings — so you can see if that $40 coat you bought is actually worth $400. fig narrows the comparables to pieces with the same brand, era, condition, and silhouette, then surfaces a recommended price + the broader market spread.",
      },
      {
        q: "What's the difference between recommended range and market spread?",
        a: "The recommended range is fig's best read of where this piece should land — tight, actionable. The market spread is the wider band of what comparable pieces have actually traded for, including outliers. Use the recommended for setting a list price; use the spread to know how much room you have to negotiate.",
      },
      {
        q: "Why is the comp range so wide?",
        a: "Vintage and resale markets are noisy. The wider the band, the less consistent recent comps are. For a tighter range, more photos + a confirmed brand tag help fig narrow in. Sometimes the answer is that the market itself is split and there's no single 'right' price.",
      },
      {
        q: "Where do comps come from?",
        a: "Public sold listings and active listings across major resale marketplaces. fig favours recent sales (last 90 days) and filters out outliers and obvious counterfeits. The 'How was this calculated?' expandable on any verdict shows a sample of the listings that fed the range.",
      },
    ],
  },
  {
    id: "closet",
    title: "Your closet & posting",
    entries: [
      {
        q: "Is fig a marketplace?",
        a: "No. fig is your closet — everything you've verified, in one place. Sales happen on the platforms you already use (Depop, Grailed, eBay, Vestiaire, etc.) and link from your profile. fig never mediates the transaction.",
      },
      {
        q: "What can I do with a scan after it's verified?",
        a: "Three roads, you choose: file it to your private archive, share it to your closet so other collectors can see it, or draft a listing for sale on your linked shop. One scan, however you want to keep it.",
      },
      {
        q: "How do I link my shop?",
        a: "Open Settings → Your shops and add the handles of the platforms you sell on — Depop, Grailed, eBay, Vestiaire. Visitors will see a 'Shops on…' row on your profile so they can find your listings off-platform. fig keeps everything in one place; the selling happens wherever you already sell.",
      },
      {
        q: "How do I mark something as sold?",
        a: "Open the piece in your closet and tap the status menu — pick 'Mark as sold,' enter the sold price and which platform it went on. It moves to the 'Sold' tab and keeps the original scan attached.",
      },
      {
        q: "Can I make my closet private?",
        a: "Yes. Settings → Privacy → 'Private account' on. Your profile, name, and handle stay visible so people can still find and follow you, but only your followers can see what you've posted.",
      },
    ],
  },
  {
    id: "finds",
    title: "Find of the Day & Find of the Week",
    entries: [
      {
        q: "What's Find of the Day?",
        a: "An algorithm-curated daily highlight on the Home tab — usually the most striking PASS-verified piece posted in the last 24 hours, weighted by community engagement and rarity. It rotates every day. There's no vote and no prize; it's an editorial nudge toward something worth looking at.",
      },
      {
        q: "What's Find of the Week?",
        a: "A weekly community vote. Every Monday at 7am PT, fig opens a shortlist of strong PASS-verified pieces from the prior week, anonymised — no handle, no title, no photo metadata. Collectors vote through Sunday 11:59pm PT, then the ballot closes and the winner is revealed. Winners earn a permanent badge and a spot in fig's weekly history.",
      },
      {
        q: "Why is the Find of the Week ballot anonymous?",
        a: "So the vote is about the piece, not the poster. Until the week closes, you can't see who owns each candidate or what they named it — just the photos, brand, era, and category. After the ballot closes, identities are revealed alongside the winner.",
      },
      {
        q: "How do I get nominated for Find of the Week?",
        a: "You don't apply — fig builds the ballot automatically every Monday. The pool is every PASS-verified piece posted in the past 7 days. From that pool fig fills 10 candidate slots in two buckets: the top 5 by community interaction (loves + comments, equal weight) plus 5 random picks from everyone else in the pool. The random half is the wildcard — it gives newer or quieter posts a real shot at the ballot even if they haven't racked up engagement yet. Post consistently, keep your verdicts PASS, and your pieces are eligible automatically.",
      },
      {
        q: "How is community interaction counted?",
        a: "Love count + comment count on the post, equal weight. Both are recomputed when the ballot is drawn (Monday 7am PT), so late-week engagement still counts. There's no boost for the seller's own loves/comments — fig pulls from the public engagement tables directly.",
      },
      {
        q: "What if there aren't enough candidates?",
        a: "Some weeks are slow. If the eligible pool is too thin to fill the random half, fig ships the week with fewer than 10 candidates rather than padding with weak picks. If the pool is empty entirely, fig marks the week 'Skipped' rather than running a vote on no material — fresh ballot the next Monday.",
      },
    ],
  },
  {
    id: "badges",
    title: "Badges & collector tier",
    entries: [
      {
        q: "What are badges?",
        a: "Identity signals you earn from real activity — not arbitrary milestones. Each badge points to something true about your collecting: deep knowledge of a brand, broad era coverage, a winning piece, or membership in the early cohort. They show up on your profile hero and on /you/badges.",
      },
      {
        q: "What's the collector tier?",
        a: "A live read of how many PASS-verified pieces are in your closet. It shows on your profile hero next to a hint of where the next tier sits. The tier name is the only thing surfaced — fig deliberately doesn't show your raw count, so the focus stays on what you own, not on grinding the number up.",
      },
      {
        q: "What does each badge mean?",
        a: "Brand Authority — most PASS-verified scans of a specific brand this week (rolling, held until someone takes the lead). Era Specialist — same idea for a decade. Diverse Collector — PASS-verified pieces across three or more decades (permanent). Find of the Week Winner — won a weekly vote (permanent). FotW Candidate — shortlisted for a weekly vote. Founder — joined while the closet was still being built (permanent).",
      },
      {
        q: "Why does a badge ring solid sometimes and dashed other times?",
        a: "Solid ring = permanent badge, you keep it forever. Dashed ring = rolling badge, held until another collector takes it. You'll see Brand Authority and Era Specialist as dashed; Diverse Collector, Founder, and FotW Winner as solid.",
      },
      {
        q: "Can I pin badges?",
        a: "Yes. On /you/badges, tap up to three badges to 'Feature' them — those are what show on your profile hero. The rest stay visible on the badges page itself.",
      },
      {
        q: "I just earned a badge — will I know?",
        a: "Yes. When a new badge lands on your account fig fires a toast — '🎉 You earned the [Badge Name] badge!' — and it shows up on your profile hero the next time it renders.",
      },
    ],
  },
  {
    id: "verified",
    title: "Verified Moderators",
    entries: [
      {
        q: "What's a Verified Moderator?",
        a: "A trusted member of the FIG community who helps keep fast fashion off the platform. Verified moderators can flag posts as fast-fashion through the report flow; three moderator flags on a single post auto-suspend it and route it to FIG admin for final review. Verified moderators are not FIG staff — they're collectors who've put in real time and have a track record we can stand behind.",
      },
      {
        q: "How do I qualify?",
        a: "Two things have to be true: (1) an active Seller or Pro Seller subscription, and (2) ten PASS-verified posts in your closet. The PASS requirement matters because it's the system's way of confirming you actually know what you're looking at — not how long you've been on the app.",
      },
      {
        q: "How do I apply once I qualify?",
        a: "When you hit both qualifications, an 'Apply to be a moderator' button appears in Settings. Tap it, answer four short questions (years collecting, your specialty area, closet size, why you want to do this), and submit. The FIG team reviews each application personally — usually within a few days. You'll get a notification when there's a decision.",
      },
      {
        q: "Will I know when I qualify?",
        a: "Yes — fig watches your activity and sends you a notification the moment you cross both qualifications. You don't have to keep checking. If you've been wondering whether you're close, the same Settings → Moderator page also tells you exactly what's missing if you're not eligible yet.",
      },
      {
        q: "What do I get if approved?",
        a: "Two things on top of the moderation tools: a permanent 'Verified Moderator' badge on your profile and posts (a trust signal for anyone shopping your closet), and +5 free scans every month while you stay an active moderator. The bonus credits land the first time you scan in each new calendar month.",
      },
      {
        q: "Can I stop being a moderator?",
        a: "Yes — email support and we'll un-verify your account. The badge goes away, the monthly scan bonus stops, and you don't lose anything else (your closet, history, paid subscription are unaffected).",
      },
    ],
  },
  {
    id: "grails",
    title: "Grails & matches",
    entries: [
      {
        q: "What's a grail?",
        a: "A specific piece you're hunting for. Add it to your grails list with a description, optional size, and a max budget. fig watches every newly-posted scan and taps you on the shoulder the moment one surfaces in the wild.",
      },
      {
        q: "How do grail matches work?",
        a: "When another collector posts a scan publicly, fig compares it against everyone's grails using brand, title, tags, and a semantic similarity score. If it lands above the threshold, you get a notification with a one-tap path to message them.",
      },
      {
        q: "Why am I not getting matches?",
        a: "Either nothing matching has been posted yet, or your grail description is too narrow or too vague. 'Vintage Levi's 501 36x32' works better than 'old jeans' or 'Levi's 501 1972 Big E selvedge made in San Francisco' (too specific). Loosen it up if you've been waiting weeks with nothing.",
      },
      {
        q: "Are my grails public?",
        a: "No. Grails are private to you. Other collectors don't see what you're hunting; they only see the message you send them when you reach out about their post.",
      },
    ],
  },
  {
    id: "feed",
    title: "Feed, messages & notifications",
    entries: [
      {
        q: "What's the feed?",
        a: "Discover verified finds from collectors whose taste you trust. The default sort is mixed — recent posts, posts from people you follow, plus Nearby finds if location sharing is on. Filter by brand, era, or interest to narrow in.",
      },
      {
        q: "How do DMs work?",
        a: "Two ways in. Tap 'Message' on any posted scan to start a thread anchored to that piece (the listing shows in the thread header). Or tap the + button in your inbox to start a general DM — search for a collector by handle and go. Threads update in realtime and live in your inbox.",
      },
      {
        q: "What does the bell icon show?",
        a: "Everything that needs your attention in one place: grail matches, new DMs, comments on your posts, reactions on your pieces, post-notification alerts, and badge announcements. Opening it marks everything as seen.",
      },
      {
        q: "How do post notifications work?",
        a: "On any collector's profile, tap the bell icon to subscribe to their posts. You'll get a ping the moment they post something new — handy for the people whose drops you don't want to miss. Toggle off the same way.",
      },
    ],
  },
  {
    id: "account",
    title: "Account, privacy & location",
    entries: [
      {
        q: "Who can see my scans?",
        a: "Private by default. A scan only shows up on the public feed once you choose to post it. You can flip a posted piece back to private anytime from the piece's page.",
      },
      {
        q: "How does location sharing work?",
        a: "Settings → Privacy → Location. The master toggle lets fig surface nearby finds in your feed. The 'Home base' field is a free-text city you type (e.g. 'Brooklyn, NY') — the Nearby feed matches you with other collectors who typed the same city. No GPS, no tracking.",
      },
      {
        q: "Does Fig implement any encryption?",
        a: "None of the algorithms mentioned in Apple's export compliance prompt. Fig uses standard HTTPS/TLS for every network call (Supabase, Stripe, Anthropic, image storage) — that encryption is handled by iOS's built-in URLSession and WebKit, not implemented inside the app. No proprietary crypto, no homegrown algorithms, no standalone crypto libraries beyond what the OS provides. The 'Help improve Fig' training-data opt-in stores scan data in plain Supabase rows (RLS-restricted), not in any custom-encrypted store.",
      },
      {
        q: "Where do I replay the walkthrough?",
        a: "Settings → 'Restart walkthrough.' It runs the six-step spotlight tour again without taking you back through onboarding.",
      },
      {
        q: "Can I change my handle?",
        a: "Yes — Settings → Profile → Username. Handles must be unique and lowercase letters / numbers / underscores only, 3–20 characters.",
      },
      {
        q: "How do I delete my account?",
        a: "Settings → Account → 'Delete account.' Two confirmations, then your profile, scans, grails, comments, follows, and messages are wiped within 30 days. Email support@figverified.com if anything goes wrong.",
      },
      {
        q: "Does fig train AI on my scans?",
        a: "Off by default. Settings → Privacy → 'Help improve fig' is the opt-in toggle. When it's on, your scans and verdict feedback help sharpen the engine; we never share your photos publicly. Off means none of your scans touch training.",
      },
    ],
  },
];

export default function FAQ() {
  const nav = useNavigate();
  // Track which entries are open. Key: `${sectionId}:${index}`.
  const [openSet, setOpenSet] = useState<Set<string>>(new Set());

  const toggle = (key: string) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <MobileFrame>
      <div className="paper-bg" style={{ minHeight: "100vh", paddingBottom: 60 }}>
        {/* Top bar */}
        <div style={{
          padding: "12px 20px 0",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <button
            onClick={() => nav(-1)}
            aria-label="Back"
            style={{ background: "none", border: "none", padding: 6, cursor: "pointer", color: "hsl(var(--ink))" }}
          >
            {Icon.chev(18, "l")}
          </button>
          <span className="caps">FAQ</span>
          <div style={{ width: 30 }} />
        </div>

        {/* Hero */}
        <div style={{ padding: "18px 20px 0" }}>
          <div style={{
            position: "relative",
            background: "hsl(var(--cream))",
            border: "1px solid hsl(var(--ink))",
            padding: "20px 18px",
          }}>
            <Tape top={-8} left="60%" rotate={4} width={62} />
            <div style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif",
              fontWeight: 900, fontSize: 28, lineHeight: 1,
              color: "hsl(var(--ink))", letterSpacing: "-0.02em",
              marginBottom: 8,
            }}>
              Field manual
            </div>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 12, color: "hsl(var(--ink-2))", lineHeight: 1.5,
            }}>
              Common questions about scanning, the reference, pricing, your closet, grails, finds, badges, verified moderators, and the feed — the stuff every collector eventually asks.
            </div>
          </div>
        </div>

        {/* Quick jump nav */}
        <div style={{ padding: "16px 20px 0" }}>
          <div className="caps" style={{ color: "hsl(var(--rust))", marginBottom: 8 }}>Jump to</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {SECTIONS.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById(s.id)?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 10,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  padding: "5px 9px",
                  border: "1px solid hsl(var(--ink))",
                  background: "hsl(var(--paper))",
                  color: "hsl(var(--ink))",
                  textDecoration: "none",
                  cursor: "pointer",
                }}
              >
                {s.title}
              </a>
            ))}
          </div>
        </div>

        {/* Sections */}
        <div style={{ padding: "10px 20px 0" }}>
          {SECTIONS.map((s) => (
            <section key={s.id} id={s.id} style={{ marginTop: 22, scrollMarginTop: 20 }}>
              <Hairline style={{ marginBottom: 10 }} />
              <div style={{
                fontFamily: "'Fraunces', 'Playfair Display', serif",
                fontWeight: 900, fontSize: 20,
                color: "hsl(var(--ink))",
                marginBottom: 10,
                letterSpacing: "-0.01em",
              }}>
                {s.title}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {s.entries.map((entry, i) => {
                  const key = `${s.id}:${i}`;
                  const open = openSet.has(key);
                  return (
                    <div
                      key={key}
                      style={{
                        border: "1px solid hsl(var(--ink))",
                        background: open ? "hsl(var(--cream))" : "hsl(var(--paper))",
                      }}
                    >
                      <button
                        onClick={() => toggle(key)}
                        aria-expanded={open}
                        style={{
                          width: "100%",
                          background: "none",
                          border: "none",
                          padding: "12px 14px",
                          cursor: "pointer",
                          display: "flex",
                          gap: 10,
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          textAlign: "left",
                          color: "hsl(var(--ink))",
                          fontFamily: "'IBM Plex Mono', monospace",
                          fontSize: 12,
                          fontWeight: 600,
                          lineHeight: 1.4,
                        }}
                      >
                        <span>{entry.q}</span>
                        <span style={{
                          flexShrink: 0,
                          color: "hsl(var(--ink-2))",
                          transform: open ? "rotate(90deg)" : "rotate(0deg)",
                          transition: "transform 120ms ease",
                          marginTop: 2,
                        }}>
                          {Icon.chev(12, "r")}
                        </span>
                      </button>
                      {open && (
                        <div style={{
                          padding: "0 14px 14px",
                          fontFamily: "'IBM Plex Mono', monospace",
                          fontSize: 12,
                          lineHeight: 1.55,
                          color: "hsl(var(--ink-2))",
                        }}>
                          {entry.a}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        {/* Footer — escape hatch to docs / contact */}
        <div style={{ padding: "30px 20px 0" }}>
          <Hairline style={{ marginBottom: 14 }} />
          <div style={{
            background: "hsl(var(--cream))",
            border: "1px solid hsl(var(--ink))",
            padding: "16px 18px",
            textAlign: "center",
          }}>
            <div className="caps" style={{ color: "hsl(var(--rust))", marginBottom: 8 }}>
              Still stuck?
            </div>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 12, color: "hsl(var(--ink-2))", lineHeight: 1.5,
              marginBottom: 12,
            }}>
              Check the field guide for deeper how-tos, or restart the tutorial from your profile.
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
              <button
                onClick={() => nav("/best-practices")}
                className="btn btn-primary"
                style={{ padding: "8px 14px", fontSize: 11 }}
              >
                Field guide
              </button>
              <button
                onClick={() => nav("/you")}
                className="btn"
                style={{ padding: "8px 14px", fontSize: 11 }}
              >
                Back to profile
              </button>
            </div>
          </div>
        </div>
      </div>
    </MobileFrame>
  );
}